import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface AnalyticsContextType {
  pageVisits: Record<string, number>;
  onlineUsers: number;
  incrementPageVisit: (page: string) => void;
  totalVisits: number;
  sessionStartTime: Date;
  getSessionDuration: () => number; // in seconds
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

/**
 * Generates a random number of online users for simulation purposes
 * In a real app, this data would come from a backend service
 * @returns A random number between 30 and 60 representing online users
 */
const getRandomOnlineUsers = () => {
  // Generate a random number between 30 and 60
  return Math.floor(Math.random() * 31) + 30;
};

/**
 * Analytics Provider component that tracks and manages analytics data
 * Provides page visit tracking, online user counts, and session duration
 * @param children - React components that will have access to the analytics context
 */
export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [pageVisits, setPageVisits] = useState<Record<string, number>>({});
  const [onlineUsers, setOnlineUsers] = useState<number>(getRandomOnlineUsers());
  const [totalVisits, setTotalVisits] = useState<number>(0);
  const [sessionStartTime] = useState<Date>(new Date());

  /**
   * Tracks page visits whenever the location changes
   */
  useEffect(() => {
    incrementPageVisit(location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /**
   * Simulates fluctuating online user counts
   * Updates the count every minute
   */
  useEffect(() => {
    const interval = setInterval(() => {
      setOnlineUsers(getRandomOnlineUsers());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  /**
   * Loads saved analytics data from localStorage on component mount
   */
  useEffect(() => {
    const savedPageVisits = localStorage.getItem('pageVisits');
    const savedTotalVisits = localStorage.getItem('totalVisits');

    if (savedPageVisits) {
      setPageVisits(JSON.parse(savedPageVisits));
    }

    if (savedTotalVisits) {
      setTotalVisits(parseInt(savedTotalVisits, 10));
    }
  }, []);

  /**
   * Saves analytics data to localStorage whenever it changes
   */
  useEffect(() => {
    localStorage.setItem('pageVisits', JSON.stringify(pageVisits));
    localStorage.setItem('totalVisits', totalVisits.toString());
  }, [pageVisits, totalVisits]);

  /**
   * Increments the visit count for a specific page
   * Also increments the total visit count across all pages
   * @param page - The page path to increment visits for
   */
  const incrementPageVisit = (page: string) => {
    setPageVisits((prev) => {
      const currentCount = prev[page] || 0;
      return {
        ...prev,
        [page]: currentCount + 1,
      };
    });
    setTotalVisits((prev) => prev + 1);
  };

  /**
   * Calculates the current session duration in seconds
   * @returns The number of seconds since the session started
   */
  const getSessionDuration = () => {
    const now = new Date();
    return Math.floor((now.getTime() - sessionStartTime.getTime()) / 1000);
  };

  return (
    <AnalyticsContext.Provider
      value={{
        pageVisits,
        onlineUsers,
        incrementPageVisit,
        totalVisits,
        sessionStartTime,
        getSessionDuration,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

/**
 * Custom hook to access the analytics context
 * @returns Analytics context with tracking functions and metrics
 * @throws Error if used outside of AnalyticsProvider
 */
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};